import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { GlassCard, Badge, NoDataState } from "../../../../ui";

const STATUS_PILL = {
  ok:    { variant: "success", label: "OK" },
  stale: { variant: "warning", label: "STALE" },
  error: { variant: "error",   label: "ERROR" },
  cold:  { variant: "neutral", label: "COLD" },
};

const FILTERS = ["all", "ok", "stale", "error", "cold"];

const formatRelative = (iso) => {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export const FleetTable = ({ rows, loading }) => {
  const history = useHistory();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("last_heartbeat");
  const [sortDir, setSortDir] = useState("desc");

  const onSort = (key) => {
    if (key === sortKey) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const visible = useMemo(() => {
    let out = rows || [];
    if (filter !== "all") out = out.filter((r) => r.status === filter);
    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter((r) =>
        (r.sme_id || "").toLowerCase().includes(s) ||
        (r.customer_name || "").toLowerCase().includes(s) ||
        (r.site_name || "").toLowerCase().includes(s),
      );
    }
    const dir = sortDir === "asc" ? 1 : -1;
    return [...out].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, filter, search, sortKey, sortDir]);

  if (!loading && (!rows || rows.length === 0)) {
    return (
      <NoDataState
        title="No mmb-edge boxes registered yet"
        description="Provision a box via mmb-edge-provision and it will appear here after the first heartbeat."
      />
    );
  }

  return (
    <Wrap>
      <Controls>
        <Search
          placeholder="Filter by SME, customer, site…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {FILTERS.map((f) => (
          <Pill
            key={f}
            active={filter === f}
            onClick={() => setFilter(f)}
          >
            {f.toUpperCase()}
          </Pill>
        ))}
      </Controls>

      <GlassCard>
        <Table>
          <thead>
            <tr>
              <Th onClick={() => onSort("sme_id")}>SME {sortKey === "sme_id" ? (sortDir === "asc" ? "↑" : "↓") : ""}</Th>
              <Th>Customer / Site</Th>
              <Th>Magnet</Th>
              <Th>Network</Th>
              <Th>App ver</Th>
              <Th onClick={() => onSort("last_heartbeat")}>Last heartbeat {sortKey === "last_heartbeat" ? (sortDir === "asc" ? "↑" : "↓") : ""}</Th>
              <Th>Rows</Th>
              <Th onClick={() => onSort("status")}>Status {sortKey === "status" ? (sortDir === "asc" ? "↑" : "↓") : ""}</Th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <Tr key={r.sme_id} onClick={() => history.push(`/admin/system/${r.sme_id}?tab=Edge`)}>
                <Td mono>{r.sme_id}</Td>
                <Td>
                  <strong>{r.customer_name || "—"}</strong>
                  <Muted>{r.site_name || ""}</Muted>
                </Td>
                <Td>{r.magnet_type || "—"}</Td>
                <Td>{r.network_type || "—"}</Td>
                <Td>{r.app_version || "—"}</Td>
                <Td>
                  {formatRelative(r.last_heartbeat)}
                  {r.error_message ? <ErrMsg>{r.error_message}</ErrMsg> : null}
                </Td>
                <Td>{r.rows_inserted ?? "—"}</Td>
                <Td>
                  <Badge variant={STATUS_PILL[r.status]?.variant || "neutral"}>
                    {STATUS_PILL[r.status]?.label || (r.status || "?").toUpperCase()}
                  </Badge>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </GlassCard>
    </Wrap>
  );
};

const Wrap = styled.div``;
const Controls = styled.div`
  display: flex; gap: 0.75rem; margin-bottom: 0.75rem; align-items: center;
`;
const Search = styled.input`
  flex: 1; padding: 0.4rem 0.6rem; border: 1px solid rgba(0,0,0,0.15); border-radius: 6px;
  background: white; font-size: 0.85rem;
`;
const Pill = styled.button`
  border: 1px solid rgba(0,0,0,0.15);
  background: ${({ active }) => (active ? "#3b82f6" : "white")};
  color: ${({ active }) => (active ? "white" : "rgba(0,0,0,0.7)")};
  padding: 0.3rem 0.7rem; border-radius: 16px; font-size: 0.75rem; cursor: pointer;
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 0.85rem;
`;
const Th = styled.th`
  text-align: left; padding: 0.6rem 0.8rem;
  font-size: 0.7rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
  color: rgba(0,0,0,0.5); border-bottom: 1px solid rgba(0,0,0,0.08); cursor: pointer;
`;
const Tr = styled.tr`
  cursor: pointer;
  &:hover { background: rgba(59,130,246,0.05); }
`;
const Td = styled.td`
  padding: 0.6rem 0.8rem;
  border-bottom: 1px solid rgba(0,0,0,0.04);
  font-family: ${({ mono }) => (mono ? "ui-monospace, monospace" : "inherit")};
`;
const Muted = styled.div`
  font-size: 0.75rem; color: rgba(0,0,0,0.55); margin-top: 0.1rem;
`;
const ErrMsg = styled.div`
  font-size: 0.7rem; color: #991b1b; font-family: ui-monospace, monospace; margin-top: 0.15rem;
`;
